/*
Copyright 2026 Element Creations Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { useEffect, useRef, useState } from "react";
import type { MatrixClient } from "matrix-js-sdk";
import styles from "./PasskeySettings.module.css";
import { useTranslation } from "react-i18next";
import { InputField } from "../input/Input";
import { localizedRequestError } from "../utils/localizedError";
import {
  passkeyCeremony,
  passkeyRequest,
  passkeyErrorMessage,
} from "./passkeys";
import { formatRecordingDateTime } from "../utils/recordingDate";

export function PasskeySettings({ client }: { client: MatrixClient }) {
  const { t } = useTranslation();
  const ceremony = useRef<AbortController | null>(null);
  useEffect(() => () => ceremony.current?.abort(), []);
  const [keys, setKeys] = useState<
    { id: string; label: string; createdAt: number }[]
  >([]);
  const [enabled, setEnabled] = useState(false),
    [busy, setBusy] = useState(false),
    [password, setPassword] = useState(""),
    [error, setError] = useState("");
  const reload = async (): Promise<void> => {
    setKeys(await passkeyRequest("", undefined, client));
  };
  useEffect(() => {
    let cancelled = false;
    void passkeyRequest("/capabilities")
      .then(async (data) => {
        if (data.enabled && window.PublicKeyCredential) {
          const values = await passkeyRequest("", undefined, client);
          if (!cancelled) {
            setKeys(values);
            setEnabled(true);
          }
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [client]);
  if (!enabled) return null;
  const add = async (): Promise<void> => {
    const controller = new AbortController();
    ceremony.current = controller;
    setBusy(true);
    setError("");
    try {
      const { flow, options } = await passkeyRequest(
        "/register/options",
        { password },
        client,
        controller.signal,
      );
      setPassword("");
      controller.signal.throwIfAborted();
      const response = await passkeyCeremony(options, true, controller.signal);
      await passkeyRequest(
        "/register/verify",
        { flow, response },
        client,
        controller.signal,
      );
      await reload();
    } catch (cause) {
      if (!controller.signal.aborted) setError(passkeyErrorMessage(cause, t));
    } finally {
      setBusy(false);
      setPassword("");
    }
  };
  return (
    <section
      onChange={(event) => event.stopPropagation()}
      style={{
        paddingTop: 4,
      }}
    >
      <h3>{t("instance.ui.passkeys")}</h3>
      <p>{t("instance.ui.passkey_setup_hint")}</p>
      <InputField
        type="password"
        label={t("instance.ui.confirm_current_password")}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        autoComplete="current-password"
        disabled={busy}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            if (!busy && password) void add();
          }
        }}
      />
      <button
        type="button"
        className="glass-button"
        style={{ padding: "10px 16px", marginTop: 12 }}
        disabled={busy || !password}
        onClick={() => void add()}
      >
        {t("instance.ui.add_passkey")}
      </button>
      {error && <p role="alert">{error}</p>}
      <ul>
        {keys.map((key) => (
          <li key={key.id} style={{ marginTop: 12, overflowWrap: "anywhere" }}>
            {key.label} · {formatRecordingDateTime(Number(key.createdAt))}{" "}
            <button
              type="button"
              className={styles.remove}
              disabled={busy}
              onClick={() => {
                setBusy(true);
                setError("");
                void passkeyRequest(
                  `/${encodeURIComponent(key.id)}/remove`,
                  {},
                  client,
                )
                  .then(reload)
                  .catch((cause) => setError(localizedRequestError(cause, t)))
                  .finally(() => setBusy(false));
              }}
            >
              {t("instance.ui.remove_passkey")}
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}
